import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Info, CheckCircle, XCircle, Search } from "lucide-react";
import { MatchDebug } from "@/hooks/useExpiredProducts";

interface MatchDebugDialogProps {
  debug: MatchDebug | null;
  issueType: string;
}

export const MatchDebugDialog = ({ debug, issueType }: MatchDebugDialogProps) => {
  if (!debug) return null;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
          <Info className="h-3 w-3 mr-1" />
          Debug
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Match Debug Info
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Result */}
          <div className="flex items-center gap-2">
            {debug.matched_via ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm">Gematcht via: <Badge variant="outline">{debug.matched_via}</Badge></span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">Geen match gevonden</span>
              </>
            )}
          </div>

          {/* Reason */}
          <div className="p-3 bg-muted rounded-md">
            <p className="text-sm">{debug.reason}</p>
          </div>

          {/* Keys tried */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Geprobeerde keys:</h4>
            
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="space-y-1">
                <div className="font-medium text-muted-foreground">SKU Raw:</div>
                <code className="block p-1 bg-muted rounded text-wrap break-all">
                  {debug.sku_raw || '(leeg)'}
                </code>
              </div>
              <div className="space-y-1">
                <div className="font-medium text-muted-foreground">SKU Key:</div>
                <code className="block p-1 bg-muted rounded text-wrap break-all">
                  {debug.sku_key || '(leeg)'}
                </code>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="space-y-1">
                <div className="font-medium text-muted-foreground">Barcode Raw:</div>
                <code className="block p-1 bg-muted rounded text-wrap break-all">
                  {debug.barcode_raw || '(leeg)'}
                </code>
              </div>
              <div className="space-y-1">
                <div className="font-medium text-muted-foreground">Barcode Key:</div>
                <code className="block p-1 bg-muted rounded text-wrap break-all">
                  {debug.barcode_key || '(leeg)'}
                </code>
              </div>
            </div>

            <div className="text-xs">
              <div className="font-medium text-muted-foreground">Barcode Digits:</div>
              <code className="block p-1 bg-muted rounded text-wrap break-all">
                {debug.barcode_digits_key || '(geen/te kort)'}
              </code>
            </div>
          </div>

          {/* Match counts */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Voorraad records gevonden:</h4>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-muted rounded">
                <div className="text-lg font-bold">{debug.voorraad_sku_count}</div>
                <div className="text-xs text-muted-foreground">SKU</div>
              </div>
              <div className="p-2 bg-muted rounded">
                <div className="text-lg font-bold">{debug.voorraad_barcode_count}</div>
                <div className="text-xs text-muted-foreground">Barcode</div>
              </div>
              <div className="p-2 bg-muted rounded">
                <div className="text-lg font-bold">{debug.voorraad_digits_count}</div>
                <div className="text-xs text-muted-foreground">Digits</div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
